<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@chatwoot/pico-search';

import Button from 'next/button/Button.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { BaseTable } from 'dashboard/components-next/table';
import McpServerRow from './components/McpServerRow.vue';
import CreateMcpServerDialog from './create/CreateMcpServerDialog.vue';
import DeleteMcpServerDialog from './delete/DeleteMcpServerDialog.vue';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const { t } = useI18n();

const searchQuery = ref('');
const createDialogRef = ref(null);
const deleteDialogRef = ref(null);
const fileInput = ref(null);
const cloningServerId = ref(null);
const exportingServerId = ref(null);

const records = computed(() => getters['wgptMcpServers/getServers'].value);
const uiFlags = computed(() => getters['wgptMcpServers/getUIFlags'].value);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name', 'description']);
});

const tableHeaders = computed(() => [
  t('WGPT_MCP_SERVERS.LIST.TABLE_HEADER.NAME'),
  t('WGPT_MCP_SERVERS.LIST.TABLE_HEADER.TOOLS'),
  t('WGPT_MCP_SERVERS.LIST.TABLE_HEADER.STATUS'),
  t('WGPT_MCP_SERVERS.LIST.TABLE_HEADER.LAST_UPDATED_AT'),
]);

const editServer = server =>
  router.push({
    name: 'wgpt_mcp_servers_edit',
    params: { serverId: server.id },
  });

const handleClone = async server => {
  cloningServerId.value = server.id;
  try {
    await store.dispatch('wgptMcpServers/clone', server.id);
    useAlert(t('WGPT_MCP_SERVERS.CLONE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('WGPT_MCP_SERVERS.CLONE.API.ERROR_MESSAGE'));
  } finally {
    cloningServerId.value = null;
  }
};

const exportServer = async id => {
  exportingServerId.value = id;
  try {
    const file = await store.dispatch('wgptMcpServers/export', id);
    const url = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    useAlert(t('WGPT_MCP_SERVERS.EXPORT.API.ERROR_MESSAGE'));
  } finally {
    exportingServerId.value = null;
  }
};

const importServer = () => fileInput.value?.click();

const handleFileChange = async event => {
  try {
    const file = event.target.files[0];
    if (!file) return;

    const server = await store.dispatch('wgptMcpServers/import', file);
    editServer(server);
  } catch (error) {
    useAlert(t('WGPT_MCP_SERVERS.IMPORT.API.ERROR_MESSAGE'));
  } finally {
    event.target.value = null;
  }
};

onMounted(() => {
  store.dispatch('wgptMcpServers/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('WGPT_MCP_SERVERS.LIST.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('WGPT_MCP_SERVERS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('WGPT_MCP_SERVERS.HEADER')"
        :description="$t('WGPT_MCP_SERVERS.DESCRIPTION')"
        :link-text="$t('WGPT_MCP_SERVERS.LEARN_MORE')"
        :search-placeholder="$t('WGPT_MCP_SERVERS.SEARCH_PLACEHOLDER')"
        feature-name="wgpt_mcp_servers"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('WGPT_MCP_SERVERS.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <div class="flex items-center flex-shrink-0 gap-4">
            <Button
              icon="i-lucide-import"
              :label="$t('WGPT_MCP_SERVERS.IMPORT_BTN_TEXT')"
              :disabled="uiFlags.isImporting"
              :is-loading="uiFlags.isImporting"
              size="sm"
              slate
              @click="importServer"
            />
            <Button
              :label="$t('WGPT_MCP_SERVERS.HEADER_BTN_TEXT')"
              size="sm"
              @click="createDialogRef?.open()"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="
          searchQuery
            ? $t('WGPT_MCP_SERVERS.NO_RESULTS')
            : $t('WGPT_MCP_SERVERS.LIST.404')
        "
      >
        <template #row="{ items }">
          <McpServerRow
            v-for="server in items"
            :key="server.id"
            :server="server"
            :is-cloning="cloningServerId === server.id"
            :is-exporting="exportingServerId === server.id"
            @delete="deleteDialogRef?.open(server)"
            @clone="handleClone(server)"
            @export="exportServer(server.id)"
          />
        </template>
      </BaseTable>
    </template>

    <CreateMcpServerDialog ref="createDialogRef" />
    <DeleteMcpServerDialog ref="deleteDialogRef" />

    <input
      ref="fileInput"
      type="file"
      accept=".wgpt"
      class="hidden"
      @change="handleFileChange"
    />
  </SettingsLayout>
</template>
